<template>
  <b-container fluid="xxl">
    <b-row class="align-items-center">
      <b-col cols="12" md="6">
        <h3>{{ $t('NAV_LINK_TEXT_ADMIN_PAGE_GROUPS') }}</h3>
      </b-col>
      <b-col cols="12" md="6" class="text-md-right mt-3 mt-md-0">
        <b-button variant="cta" @click="openEditModal(null)">
          Neue Page-Gruppe
        </b-button>
      </b-col>
    </b-row>

    <b-table
      striped
      hover
      small
      class="mt-4"
      :fields="fields"
      :items="pageGroups"
      show-empty
    >
      <template #empty>
        <p class="w-100 text-center"><small>{{ $t('EMPTY_ITEMS_TABLE_MSG_PAGE_GROUPS') }}</small></p>
      </template>

      <template #cell(col_group_info)="{ item }">
        <div class="d-flex align-items-center">
          <LucideIcon v-if="isFaIcon(item.page_icon)" :name="item.page_icon" class="mr-2" :size="20" />
          <b-avatar v-else-if="isUrlIcon(item.page_icon)" :src="item.page_icon" size="2rem" class="mr-2"/>
          <div>
            <div class="font-weight-bold">{{ item.page_group_title }}</div>
            <div class="text-muted small">{{ item.page_group_subtitle }}</div>
          </div>
        </div>
      </template>

      <template #cell(col_active)="{ item }">
        <span v-if="Number(item.is_active) === 1" class="text-success">
          <font-awesome-icon :icon="['fa', 'smile']"/>
        </span>
        <span v-else class="text-danger">
          <font-awesome-icon :icon="['fa', 'skull']"/>
        </span>
      </template>

      <template #cell(col_tags)="{ item }">
        <div>
          <b-badge
            v-for="t in sortTags(item.tags)"
            :key="t"
            variant="light"
            class="mr-1 mb-1"
          >{{ t }}</b-badge>
        </div>
      </template>

      <template #cell(col_actions)="{ item }">
        <b-button size="sm" variant="light" @click="openEditModal(item.page_group_id)" title="Editieren">
          <font-awesome-icon :icon="['fa', 'pen']"/>
        </b-button>
      </template>
    </b-table>

    <b-modal id="modal-edit" v-model="showEdit" :title="modalTitle" @hidden="resetForm" hide-footer>
      <b-form @submit.prevent="onSubmit">
        <b-form-group label="Titel" label-for="pg-title" label-cols="4">
          <b-form-input id="pg-title" v-model.trim="form.page_group_title" required></b-form-input>
        </b-form-group>
        <b-form-group label="Kurztext" label-for="pg-sub" label-cols="4">
          <b-form-textarea id="pg-sub" v-model.trim="form.page_group_subtitle" required rows="3"></b-form-textarea>
        </b-form-group>
        <b-form-group label="Page-Adresse" label-for="pg-slug" label-cols="4">
          <b-input-group>
            <b-form-input id="pg-slug" v-model.trim="form.page_slug" :state="slugState" placeholder="z. B. mein-gruppenname"></b-form-input>
            <b-input-group-append>
              <b-button variant="light" :title="'Slug aus Titel übernehmen'" @click="fillPageGroupSlugFromTitle">
                <span class="mr-1" aria-hidden="true">
                  <font-awesome-icon :icon="['fa','fill-drip']"/>
                </span>
                <span class="sr-only">Slug aus Titel übernehmen</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <b-form-invalid-feedback :state="slugState">Erlaubt sind nur Buchstaben (a–z, A–Z), Ziffern (0–9) und Bindestrich (-).
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Icon" label-for="pg-icon" label-cols="4">
          <b-form-input id="pg-icon" v-model.trim="form.page_icon"></b-form-input>
          <b-form-text>
            <a href="https://lucide.dev/icons" target="_blank" rel="noopener" class="small">Lucide Icons öffnen</a>
          </b-form-text>
        </b-form-group>
        <b-form-group label="Aktivieren / deaktivieren" label-cols="4" label-class="d-flex align-items-center" content-class="d-flex align-items-center">
          <b-form-radio-group v-model="form.is_active" :options="activeOptions" name="is-active" :stacked="false" class="mb-0" buttons button-variant="outline-cta">
          </b-form-radio-group>
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="showEdit=false">Abbrechen</b-button>
          <b-button type="submit" variant="cta" :disabled="submitting">Speichern</b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal id="modal-result" v-model="showResult" :title="resultTitle" ok-only ok-variant="cta" @ok="afterResultOk">
      <div v-html="resultMessage"></div>
    </b-modal>
  </b-container>
</template>

<script>
import axios from 'axios'

const LucideIcon = {
  name: 'LucideIcon',
  props: {
    name: { type: String, required: true },
    size: { type: [Number, String], default: 16 },
  },
  computed: {
    kebabName() {
      const n = (this.name || '').toString().trim();
      if (!n) return '';
      // convert common FA-like names to lucide kebab-case
      return n
        .replace(/_/g, '-')
        .replace(/\s+/g, '-')
        .replace(/[A-Z]/g, m => '-' + m.toLowerCase())
        .replace(/^-+/, '')
        .toLowerCase();
    },
    url() {
      // Use same-origin local assets to comply with CSP. Place SVGs under /public/lucide/icons
      // Example: app/app/public/lucide/icons/activity.svg => available at /lucide/icons/activity.svg
      const base = (process.env.BASE_URL || '').replace(/\/+$/, '');
      return `${base}/lucide/icons/${this.kebabName}.svg`;
    },
    px() {
      const s = Number(this.size);
      return isNaN(s) ? 16 : s;
    },
    styleObj() {
      return {
        width: this.px + 'px',
        height: this.px + 'px',
        display: 'inline-block',
        backgroundColor: 'currentColor',
        WebkitMask: `url(${this.url}) center / contain no-repeat`,
        mask: `url(${this.url}) center / contain no-repeat`,
        verticalAlign: 'middle'
      };
    }
  },
  render(h) {
    return h('span', { class: ['lucide-icon'], style: this.styleObj, attrs: { 'aria-hidden': 'true' } });
  }
};

export default {
  name: 'areaAdminPageGroups',
  components: { LucideIcon },
  data() {
    return {
      pageGroups: [],
      fields: [
        { key: 'col_group_info', label: 'Page-Gruppe' },
        { key: 'col_active', label: 'Status', class: 'text-center' },
        { key: 'col_tags', label: 'Page-Tags' },
        { key: 'col_actions', label: '', class: 'text-right' }
      ],
      showEdit: false,
      submitting: false,
      form: {
        page_group_id: null,
        page_group_title: '',
        page_group_subtitle: '',
        page_slug: '',
        page_icon: '',
        is_active: '0'
      },
      activeOptions: [
        { text: 'inaktiv', value: '0' },
        { text: 'aktiv', value: '1' }
      ],
      showResult: false,
      resultTitle: '',
      resultMessage: '',
    }
  },
  computed: {
    modalTitle() {
      return this.form.page_group_id ? 'Page-Gruppe bearbeiten' : 'Neue Page-Gruppe';
    },
    slugState() {
      // null for untouched/empty, true for valid, false for invalid
      const s = (this.form.page_slug || '').trim();
      if (s === '') return null;
      const re = /^[a-zA-Z0-9-]+$/;
      return re.test(s);
    }
  },
  methods: {
    async fetchPageGroups() {
      try {
        const response = await axios.post(process.env.VUE_APP_API, { type: 'get_page_groups', cookie: this.$store.getters.getToken });
        // Expecting array of groups with fields incl. page_tags list
        this.pageGroups = Array.isArray(response.data) ? response.data : [];
      } catch (e) {
        this.pageGroups = [];
      }
    },
    sortTags(tags) {
      if (!tags) return [];
      let names = [];
      if (Array.isArray(tags)) {
        names = tags.map(t => (t && typeof t === 'object' && 'page_tag' in t) ? t.page_tag : t).filter(Boolean);
      } else {
        names = String(tags).split(',').map(s => s.trim()).filter(Boolean);
      }
      return names.map(s => String(s)).sort((a,b)=>a.localeCompare(b, 'de', { sensitivity: 'base' }));
    },
    isUrlIcon(val) {
      if (!val) return false;
      const s = String(val).trim();
      const re = new RegExp('^(https?:\\/\\/|\\/|data:image\\/)', 'i');
      return re.test(s);
    },
    isFaIcon(val) {
      if (!val) return false;
      // Consider as FA icon if it's not a URL-like value
      return !this.isUrlIcon(val);
    },
    async openEditModal(page_group_id) {
      this.resetForm();
      if (page_group_id) {
        try {
          const response = await axios.post(process.env.VUE_APP_API, { type: 'get_page_group', page_group_id, cookie: this.$store.getters.getToken });
          const pg = Array.isArray(response.data) ? response.data[0] : response.data;
          if (pg) {
            this.form = {
              page_group_id: Number(pg.page_group_id) || null,
              page_group_title: pg.page_group_title || '',
              page_group_subtitle: pg.page_group_subtitle || '',
              page_slug: pg.page_slug || '',
              page_icon: pg.page_group_icon || pg.page_icon || '',
              is_active: String(pg.is_active) === '1' ? '1' : '0'
            };
          }
        } catch (e) {
          // ignore
        }
      }
      this.showEdit = true;
    },
    resetForm() {
      this.form = {
        page_group_id: null,
        page_group_title: '',
        page_group_subtitle: '',
        page_slug: '',
        page_icon: '',
        is_active: '0'
      };
      this.submitting = false;
    },
    slugify(str) {
      if (!str) return '';
      let s = String(str).trim();
      try {
        s = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
      } catch (e) { /* ignore */ }
      s = s.replace(/[^a-zA-Z0-9]+/g, '-');
      s = s.replace(/-+/g, '-');
      s = s.replace(/^-|-$/g, '');
      s = s.toLowerCase();
      return s;
    },
    fillPageGroupSlugFromTitle() {
      const generated = this.slugify(this.form.page_group_title || '');
      this.form.page_slug = generated;
    },
    validateForm() {
      const titleOk = this.form.page_group_title.trim() !== '';
      const subOk = this.form.page_group_subtitle.trim() !== '';
      const slug = (this.form.page_slug || '').trim();
      const slugOk = slug === '' || /^[a-zA-Z0-9-]+$/.test(slug);
      return titleOk && subOk && slugOk;
    },
    async onSubmit() {
      if (!this.validateForm()) {
        this.resultTitle = this.$t('ERROR_TITLE_DEFAULT');
        this.resultMessage = 'Bitte fülle alle erforderlichen Felder aus.';
        this.showResult = true;
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          type: 'page_group_edit',
          data: {
            page_group_id: this.form.page_group_id,
            page_group_title: this.form.page_group_title,
            page_group_subtitle: this.form.page_group_subtitle,
            page_slug: this.form.page_slug,
            page_icon: this.form.page_icon,
            is_active: Number(this.form.is_active),
          },
          cookie: this.$store.getters.getToken
        };
        const response = await axios.post(process.env.VUE_APP_API, payload, { withCredentials: true });
        const ok = response && response.data && Number(response.data.page_group_id || response.data) > 0;
        this.resultTitle = ok ? this.$t('SUCCESS_TITLE_DEFAULT') : this.$t('ERROR_TITLE_DEFAULT');
        this.resultMessage = ok ? this.$t('SUCCESS_TEXT_SAVE_DATA') : (response.data && response.data.error ? response.data.error : 'Unbekannter Fehler');
        this.showResult = true;
        if (ok) {
          this.showEdit = false;
          await this.fetchPageGroups();
        }
      } catch (e) {
        this.resultTitle = this.$t('ERROR_TITLE_DEFAULT');
        this.resultMessage = e?.message || 'Fehler beim Speichern';
        this.showResult = true;
      } finally {
        this.submitting = false;
      }
    },
    afterResultOk() {
      this.showResult = false;
    }
  },
  mounted() {
    this.fetchPageGroups();
  }
}
</script>

<style scoped>
/* Improve visibility: selected radio button (outline-cta) appears filled, unselected remains outline */
.btn-outline-cta.active,
.btn-outline-cta:active,
.btn-outline-cta.active:focus,
.btn-outline-cta:focus,
.btn-outline-cta:hover {
  background-color: var(--cta, #007bff);
  border-color: var(--cta, #007bff);
  color: #fff !important;
}

/* Keep unselected outline buttons with proper text color */
.btn-outline-cta {
  color: var(--cta, #007bff);
}
</style>
